import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getComplianceSnapshot } from '../../services/dashboardService';
import { getSchools } from '../../services/schoolService';
import { useAuth } from '../../context/AuthContext';
import NavIcon from '../common/NavIcon';
import { initials } from '../common/Sidebar';

const EMPTY = {
  stats: { active_ieps: 0, due_in_30_days: 0, reevaluations_pending: 0, high_risk_students: 0 },
  summary: {
    new_this_month: 0, active_trend: [], due_this_week: 0, overdue: 0,
    reeval_urgency: { under_10: 0, days_10_30: 0, over_30: 0 },
    high_risk_overdue: 0, high_risk_non_compliant: 0, managers_due: [],
  },
  health: { compliant: 0, in_review: 0, non_compliant: 0, not_run: 0 },
  top_issues: [],
  attention: { items: [], total: 0 },
  deadlines: [],
  tab_counts: { all: 0, overdue: 0, due_soon: 0, high_risk: 0 },
  activity: { rows: [], total: 0, page: 1, total_pages: 0 },
};

const PAGE_SIZE = 7;

const ROW_STATUS = {
  non_compliant: { label: 'Non-compliant', icon: '✕', pill: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300', bar: 'bg-red-600' },
  at_risk: { label: 'At risk', icon: '!', pill: 'bg-[#ffedd5] text-[#9a3412] dark:bg-[#f97316]/15 dark:text-[#fdba74]', bar: 'bg-amber-500' },
  in_review: { label: 'In review', icon: '◔', pill: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300', bar: 'bg-blue-500' },
  compliant: { label: 'Compliant', icon: '✓', pill: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300', bar: 'bg-green-600' },
  not_run: { label: 'Not run', icon: '–', pill: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300', bar: 'bg-slate-400' },
};

const HEALTH = [
  { key: 'compliant', label: 'Compliant', color: '#16a34a' },
  { key: 'in_review', label: 'Needs review', color: '#3b82f6' },
  { key: 'non_compliant', label: 'Non-compliant', color: '#dc2626' },
  { key: 'not_run', label: 'Not yet run', color: '#cbd5e1' },
];

const ATTENTION_STYLE = {
  overdue: { icon: '!', tile: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
  reeval: { icon: '!', tile: 'bg-[#ffedd5] text-[#c2410c] dark:bg-[#f97316]/15 dark:text-[#fdba74]' },
  review: { icon: '?', tile: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_soon', label: 'Due soon' },
  { key: 'high_risk', label: 'High risk' },
];

const AVATAR_TINTS = [
  'bg-indigo-100 text-indigo-800', 'bg-pink-100 text-pink-800', 'bg-green-100 text-green-800',
  'bg-amber-100 text-amber-800', 'bg-sky-100 text-sky-800', 'bg-violet-100 text-violet-800', 'bg-rose-100 text-rose-800',
];

function parseDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(iso) {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';
}

function daysFromToday(iso) {
  const d = parseDate(iso);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function relativeDue(iso) {
  const n = daysFromToday(iso);
  if (n == null) return { text: '', late: false };
  if (n < 0) return { text: `Overdue ${-n} day${n === -1 ? '' : 's'}`, late: true };
  if (n === 0) return { text: 'Due today', late: true };
  return { text: `in ${n} day${n === 1 ? '' : 's'}`, late: false };
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

const Card = React.forwardRef(function Card({ className = '', children, ...rest }, ref) {
  return (
    <section ref={ref} className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[14px] ${className}`} {...rest}>
      {children}
    </section>
  );
});

function Select({ label, value, onChange, children }) {
  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none max-w-[230px] truncate h-[34px] rounded-[10px] border border-slate-300 bg-white pl-3 pr-8 text-[13px] font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200"
      >
        {children}
      </select>
      <NavIcon name="chevronDown" className="w-3.5 h-3.5 absolute right-2.5 text-slate-500 pointer-events-none" strokeWidth={2} />
    </label>
  );
}

function Sparkline({ values }) {
  if (!values || values.length < 2) return <span className="w-[110px] h-[34px]" />;
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * 110},${30 - ((v - min) / span) * 24}`).join(' ');
  return (
    <svg width="110" height="34" viewBox="0 0 110 34" className="ml-auto" aria-hidden="true">
      <polyline fill="rgba(79,70,229,.08)" stroke="none" points={`${pts} 110,34 0,34`} />
      <polyline fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

function Kpi({ label, value, valueClass = 'text-slate-900 dark:text-white', visual, foot, tone }) {
  return (
    <Card className={`p-4 ${tone === 'danger' ? 'border-red-200 bg-gradient-to-b from-white to-red-50/60 dark:border-red-500/30 dark:from-slate-900 dark:to-red-500/5' : ''}`}>
      <p className={`text-xs font-medium ${tone === 'danger' ? 'text-red-800 dark:text-red-300' : 'text-slate-500 dark:text-slate-400'}`}>{label}</p>
      <div className="flex items-end gap-2.5 mt-1.5 min-h-[34px]">
        <p className={`text-[30px] font-bold leading-none tracking-tight tabular-nums ${valueClass}`}>{value}</p>
        {visual}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">{foot}</div>
    </Card>
  );
}

function SkeletonBlock({ className }) {
  return <div className={`animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800 ${className}`} />;
}

function toCsv(rows) {
  const head = ['Student', 'Student ID', 'School', 'Type', 'Due date', 'Score', 'Status', 'Case manager'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [
    r.name, r.student_id, r.school_name, r.type, formatDate(r.due_date),
    r.compliance_score ?? '', (ROW_STATUS[r.row_status] || ROW_STATUS.not_run).label, r.manager,
  ].map(esc).join(','));
  return [head.map(esc).join(','), ...lines].join('\r\n');
}

export default function DirectorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDirector = user?.role === 1;
  const [snapshot, setSnapshot] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [windowDays, setWindowDays] = useState('30');
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const tableRef = useRef(null);
  const firstLoad = useRef(true);

  const params = useCallback((extra = {}) => {
    const p = { page, limit: PAGE_SIZE, tab, window_days: Number(windowDays), ...extra };
    if (schoolId) p.school_id = Number(schoolId);
    if (search.trim()) p.search = search.trim();
    return p;
  }, [page, tab, windowDays, schoolId, search]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      if (firstLoad.current) setLoading(true); else setTableLoading(true);
      setError('');
      getComplianceSnapshot(params())
        .then(({ data }) => { if (alive) setSnapshot({ ...EMPTY, ...(data?.data || {}) }); })
        .catch(() => { if (alive) setError('Could not load the compliance snapshot. Try again in a moment.'); })
        .finally(() => {
          if (!alive) return;
          firstLoad.current = false;
          setLoading(false);
          setTableLoading(false);
        });
    }, search ? 300 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [params, search]);

  useEffect(() => {
    if (!isDirector) return;
    getSchools({ page: 1, limit: 100, status: 'active' })
      .then(({ data }) => setSchools(data?.data?.schools || data?.data?.items || []))
      .catch(() => {});
  }, [isDirector]);

  const { stats, summary, health, top_issues: topIssues, attention, deadlines, tab_counts: tabCounts, activity } = snapshot;
  const rows = activity.rows || [];

  const openStudent = (id) => {
    navigate(`/students/${id}/information`);
  };

  const showTab = (key) => {
    setTab(key);
    setPage(1);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = [];
      for (let p = 1; p < 50; p += 1) {
        const { data } = await getComplianceSnapshot(params({ page: p, limit: 100 }));
        const act = data?.data?.activity || {};
        all.push(...(act.rows || []));
        if (p >= (act.total_pages || 1)) break;
      }
      const blob = new Blob([toCsv(all)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-snapshot-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const evaluated = health.compliant + health.in_review + health.non_compliant;
  const compliantPct = evaluated ? Math.round((health.compliant / evaluated) * 100) : null;
  const healthTotal = HEALTH.reduce((s, h) => s + (health[h.key] || 0), 0);
  const donut = useMemo(() => {
    if (!healthTotal) return '#e2e8f0 0 100%';
    let acc = 0;
    return HEALTH.map((h) => {
      const start = acc;
      acc += ((health[h.key] || 0) / healthTotal) * 100;
      return `${h.color} ${start}% ${acc}%`;
    }).join(', ');
  }, [health, healthTotal]);
  const maxIssue = Math.max(1, ...topIssues.map((i) => i.count));
  const urgency = summary.reeval_urgency || EMPTY.summary.reeval_urgency;
  const urgencyTotal = urgency.under_10 + urgency.days_10_30 + urgency.over_30;
  const managers = summary.managers_due || [];
  const schoolCount = schools.length;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const deadlineGroups = useMemo(() => {
    const groups = [['Overdue', []], ['This week', []], ['Next week', []], ['Later', []]];
    deadlines.forEach((d) => {
      const n = daysFromToday(d.due_date);
      const idx = d.overdue || n < 0 ? 0 : n <= 7 ? 1 : n <= 14 ? 2 : 3;
      groups[idx][1].push(d);
    });
    return groups.filter(([, items]) => items.length);
  }, [deadlines]);

  if (loading) {
    return (
      <div className="w-full min-w-0 space-y-[18px] py-1" aria-busy="true">
        <SkeletonBlock className="h-12 w-80" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3.5">{[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-[124px]" />)}</div>
        <div className="grid xl:grid-cols-[1.35fr_1fr] gap-3.5"><SkeletonBlock className="h-72" /><SkeletonBlock className="h-72" /></div>
        <SkeletonBlock className="h-96" />
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 flex flex-col gap-[18px] py-1">
      {/* Greeting + filters */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            {greeting()}{user?.first_name ? `, ${user.first_name}` : ''}
          </h1>
          <p className="text-[13.5px] text-slate-500 dark:text-slate-400 mt-0.5">
            {today} · {isDirector
              ? `Compliance across ${schoolId ? 'this school' : `${schoolCount || 'all'} school${schoolCount === 1 ? '' : 's'}`} and ${stats.active_ieps} active IEP${stats.active_ieps === 1 ? '' : 's'}`
              : `Your caseload: ${stats.active_ieps} active IEP${stats.active_ieps === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isDirector && (
            <Select label="School" value={schoolId} onChange={(v) => { setSchoolId(v); setPage(1); }}>
              <option value="">All schools</option>
              {schools.map((s) => <option key={s.id} value={s.id}>{s.school_name || s.name}</option>)}
            </Select>
          )}
          <Select label="Deadline window" value={windowDays} onChange={(v) => { setWindowDays(v); setPage(1); }}>
            <option value="30">Next 30 days</option>
            <option value="60">Next 60 days</option>
            <option value="90">Next 90 days</option>
          </Select>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting || !activity.total}
            className="inline-flex items-center gap-1.5 h-[34px] rounded-[10px] border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <NavIcon name="download" className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">{error}</div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
        <Kpi
          label="Active IEPs"
          value={stats.active_ieps}
          visual={<Sparkline values={summary.active_trend} />}
          foot={summary.new_this_month
            ? <span className="font-medium text-green-700 dark:text-green-400">▲ {summary.new_this_month} <span className="font-normal text-slate-500 dark:text-slate-400">new this month</span></span>
            : 'No new students this month'}
        />
        <Kpi
          label={`Due in ${windowDays} days`}
          value={stats.due_in_30_days}
          valueClass="text-[#c2410c] dark:text-[#fb923c]"
          visual={managers.length > 0 && (
            <span className="ml-auto flex pl-1.5">
              {managers.slice(0, 3).map((m, i) => (
                <span key={m.name} title={`${m.name} · ${m.count} due`}
                  className={`-ml-1.5 w-[26px] h-[26px] rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center text-[10px] font-bold ${AVATAR_TINTS[i]}`}>
                  {m.initials}
                </span>
              ))}
              {managers.length > 3 && (
                <span className="-ml-1.5 w-[26px] h-[26px] rounded-full border-2 border-white dark:border-slate-900 bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-bold">+{managers.length - 3}</span>
              )}
            </span>
          )}
          foot={<><b className="text-slate-900 dark:text-white">{summary.due_this_week}</b> this week · <b className="text-slate-900 dark:text-white">{summary.overdue}</b> overdue</>}
        />
        <Kpi
          label="Reevaluations pending"
          value={stats.reevaluations_pending}
          visual={(
            <span className="ml-auto w-[120px]" aria-hidden="true">
              <span className="flex h-2 rounded-full overflow-hidden gap-0.5 bg-slate-100 dark:bg-slate-800">
                {urgencyTotal > 0 && (
                  <>
                    <span className="bg-[#f97316]" style={{ flex: urgency.under_10 }} />
                    <span className="bg-yellow-400" style={{ flex: urgency.days_10_30 }} />
                    <span className="bg-slate-300 dark:bg-slate-600" style={{ flex: urgency.over_30 }} />
                  </>
                )}
              </span>
              <span className="flex justify-between text-[10px] text-slate-400 mt-[3px]"><span>&lt;10 d</span><span>10–30</span><span>30+</span></span>
            </span>
          )}
          foot={<>45-day window · <b className="text-[#c2410c] dark:text-[#fb923c]">{urgency.under_10}</b> close in under 10 days</>}
        />
        <Kpi
          tone="danger"
          label="High-risk students"
          value={stats.high_risk_students}
          valueClass="text-red-700 dark:text-red-400"
          visual={stats.high_risk_students > 0 && (
            <button type="button" onClick={() => showTab('high_risk')} className="ml-auto text-xs font-semibold text-red-700 hover:underline dark:text-red-400">View list →</button>
          )}
          foot={`${summary.high_risk_overdue} overdue review${summary.high_risk_overdue === 1 ? '' : 's'} · ${summary.high_risk_non_compliant} non-compliant`}
        />
      </div>

      {/* Needs attention + compliance health */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-3.5">
        <Card className="px-[18px] py-4">
          <div className="flex items-center mb-2">
            <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white">Needs attention</h2>
            {attention.total > 0 && (
              <span className="ml-2 rounded-full bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300 px-2 text-[11.5px] font-semibold leading-5">{attention.total}</span>
            )}
            {attention.total > attention.items.length && (
              <button type="button" onClick={() => showTab('high_risk')} className="ml-auto text-[12.5px] font-medium text-indigo-700 hover:underline dark:text-indigo-300">See all</button>
            )}
          </div>
          {attention.items.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Nothing needs attention right now.</p>
          ) : attention.items.map((a, i) => {
            const style = ATTENTION_STYLE[a.kind] || ATTENTION_STYLE.review;
            return (
              <div key={`${a.kind}-${a.student_db_id}-${i}`} className="flex items-center gap-3 py-2.5 border-t border-slate-100 dark:border-slate-800">
                <span className={`w-[30px] h-[30px] rounded-[9px] flex items-center justify-center text-[13px] font-bold flex-shrink-0 ${style.tile}`} aria-hidden="true">{style.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-semibold text-slate-900 dark:text-white truncate">{a.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{a.detail}</p>
                </div>
                <button type="button" onClick={() => openStudent(a.student_db_id)}
                  className="h-[30px] rounded-[10px] border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 whitespace-nowrap">
                  {a.action}
                </button>
              </div>
            );
          })}
        </Card>

        <Card className="px-[18px] py-4">
          <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white mb-3">Compliance health</h2>
          <div className="flex items-center gap-[18px]">
            <div className="w-[116px] h-[116px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `conic-gradient(${donut})` }}
              role="img" aria-label={`${compliantPct ?? 0}% of evaluated IEPs compliant`}>
              <div className="w-[84px] h-[84px] rounded-full bg-white dark:bg-slate-900 flex flex-col items-center justify-center">
                <span className="text-[22px] font-bold text-slate-900 dark:text-white">{compliantPct == null ? '—' : `${compliantPct}%`}</span>
                <span className="text-[10.5px] text-slate-500 dark:text-slate-400">compliant</span>
              </div>
            </div>
            <dl className="flex-1 grid grid-cols-[1fr_auto] gap-x-2.5 gap-y-[7px] text-[12.5px]">
              {HEALTH.map((h) => (
                <React.Fragment key={h.key}>
                  <dt className="flex items-center gap-[7px] text-slate-700 dark:text-slate-300">
                    <span className="w-[9px] h-[9px] rounded-[3px]" style={{ background: h.color }} />{h.label}
                  </dt>
                  <dd className="font-bold text-slate-900 dark:text-white tabular-nums">{health[h.key] || 0}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
          <p className="mt-3.5 text-xs font-semibold text-slate-600 dark:text-slate-300">Most common non-compliant sections</p>
          {topIssues.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">No non-compliant sections.</p>
          ) : (
            <div className="flex flex-col gap-[7px] mt-2">
              {topIssues.map((i) => (
                <div key={i.section} className="grid grid-cols-[1fr_120px_24px] gap-2.5 items-center text-[12.5px]">
                  <span className="truncate text-slate-700 dark:text-slate-300">{i.label}</span>
                  <span className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                    <span className="block h-full rounded-full bg-indigo-500" style={{ width: `${(i.count / maxIssue) * 100}%` }} />
                  </span>
                  <b className="text-right text-slate-900 dark:text-white tabular-nums">{i.count}</b>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Deadlines + activity */}
      <div className="grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)] gap-3.5">
        <Card className="px-[18px] py-4">
          <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white mb-1">Upcoming deadlines</h2>
          {deadlineGroups.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">No deadlines in the next {windowDays} days.</p>
          )}
          {deadlineGroups.map(([label, items]) => (
            <div key={label}>
              <p className="pt-3 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-slate-400">{label}</p>
              {items.map((d) => {
                const dt = parseDate(d.due_date);
                const urgent = label === 'Overdue';
                return (
                  <button key={`${d.student_db_id}-${d.due_date}`} type="button" onClick={() => openStudent(d.student_db_id, d.name)}
                    className="w-full flex items-center gap-[11px] py-1.5 text-left rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <span className={`w-10 text-center rounded-[9px] border py-[3px] flex-shrink-0 ${urgent ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'}`}>
                      <span className={`block text-[9.5px] font-semibold uppercase ${urgent ? 'text-red-700 dark:text-red-300' : 'text-slate-500'}`}>{dt?.toLocaleDateString('en-US', { month: 'short' })}</span>
                      <span className="block text-[15px] font-bold leading-tight text-slate-900 dark:text-white">{dt?.getDate()}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-slate-900 dark:text-white truncate">{d.name}</span>
                      <span className="block text-[11.5px] text-slate-500 dark:text-slate-400">{d.type}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </Card>

        <Card ref={tableRef} className="overflow-hidden scroll-mt-20">
          <div className="flex flex-wrap items-center gap-2 px-3.5 py-3">
            <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white mr-2.5 ml-1">Recent compliance activity</h2>
            <div role="tablist" aria-label="Filter activity" className="flex flex-wrap gap-1">
              {TABS.map((t) => (
                <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => { setTab(t.key); setPage(1); }}
                  className={`rounded-lg px-2.5 py-[5px] text-[12.5px] font-medium transition-colors ${
                    tab === t.key ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
                  }`}>
                  {t.label}{t.key !== 'all' ? ` · ${tabCounts[t.key] ?? 0}` : ''}
                </button>
              ))}
            </div>
            <label className="ml-auto flex items-center gap-2 w-full sm:w-[200px] h-[32px] rounded-[9px] border border-slate-200 px-2.5 text-[12.5px] text-slate-400 focus-within:border-indigo-400 dark:border-slate-700">
              <NavIcon name="search" className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="sr-only">Filter students</span>
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Filter students"
                className="flex-1 min-w-0 bg-transparent text-slate-900 placeholder-slate-400 outline-none dark:text-white" />
            </label>
          </div>

          <div className={`overflow-x-auto transition-opacity ${tableLoading ? 'opacity-50' : ''}`}>
            <table className="w-full min-w-[760px] border-collapse">
              <thead className="bg-slate-50 dark:bg-slate-800/60">
                <tr className="text-left text-[11px] font-medium uppercase tracking-[0.04em] text-slate-500 dark:text-slate-400">
                  <th className="px-2.5 py-[9px] font-medium">Student</th>
                  <th className="px-2.5 py-[9px] font-medium">Date of birth</th>
                  <th className="px-2.5 py-[9px] font-medium">Type</th>
                  <th className="px-2.5 py-[9px] font-medium">Due</th>
                  <th className="px-2.5 py-[9px] font-medium">Score</th>
                  <th className="px-2.5 py-[9px] font-medium">Status</th>
                  <th className="px-2.5 py-[9px] font-medium">Case manager</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">No students match these filters.</td></tr>
                )}
                {rows.map((r, i) => {
                  const st = ROW_STATUS[r.row_status] || ROW_STATUS.not_run;
                  const rel = relativeDue(r.due_date);
                  const score = Math.max(0, Math.min(100, Number(r.compliance_score) || 0));
                  return (
                    <tr key={r.student_db_id} onClick={() => openStudent(r.student_db_id, r.name, r.overall_status)}
                      className="border-t border-slate-100 dark:border-slate-800 text-[13px] cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-2.5 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${AVATAR_TINTS[i % AVATAR_TINTS.length]}`}>{initials(r.name)}</span>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 dark:text-white truncate max-w-[10.5rem]">{r.name}</p>
                            <p className="text-[11.5px] text-slate-400 truncate max-w-[10.5rem]">#{r.student_id}{r.school_name ? ` · ${r.school_name}` : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2.5 py-2.5 whitespace-nowrap text-slate-700 dark:text-slate-200">{formatDate(r.date_of_birth)}</td>
                      <td className="px-2.5 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">{r.type}</td>
                      <td className="px-2.5 py-2.5 whitespace-nowrap">
                        <p className="font-medium text-slate-900 dark:text-white">{formatDate(r.due_date)}</p>
                        <p className={`text-[11.5px] ${rel.late ? 'font-semibold text-red-700 dark:text-red-400' : 'text-slate-500'}`}>{rel.text}</p>
                      </td>
                      <td className="px-2.5 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="w-[44px] h-[5px] rounded-full bg-slate-100 dark:bg-slate-800">
                            <span className={`block h-full rounded-full ${st.bar}`} style={{ width: `${score}%` }} />
                          </span>
                          <span className="text-xs text-slate-600 dark:text-slate-300 tabular-nums">{r.compliance_score ?? '—'}</span>
                        </div>
                      </td>
                      <td className="px-2.5 py-2.5">
                        <span className={`inline-flex items-center gap-[5px] rounded-full px-[9px] py-0.5 text-[11.5px] font-semibold whitespace-nowrap ${st.pill}`}>
                          <span aria-hidden="true">{st.icon}</span>{st.label}
                        </span>
                      </td>
                      <td className="px-2.5 py-2.5 max-w-[11rem]">
                        <span className="block text-[12.5px] leading-snug text-slate-700 dark:text-slate-200 whitespace-normal break-words">
                          {r.manager || '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-3.5 py-2.5 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
            <span>Showing {rows.length} of {activity.total}</span>
            <span className="flex items-center gap-1.5">
              <span className="mr-1">Page {activity.total_pages ? page : 0} of {activity.total_pages || 0}</span>
              <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="h-7 w-8 rounded-lg border border-slate-300 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300">‹</button>
              <button type="button" aria-label="Next page" disabled={page >= (activity.total_pages || 1)} onClick={() => setPage((p) => p + 1)}
                className="h-7 w-8 rounded-lg border border-slate-300 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300">›</button>
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
